'use client';

import { type CommentDto } from '@/domain/roadmap/types';
import { useRoadmapEditStore } from '@/features/roadmap/store/roadmap';
import { useAuth } from '@/hooks/useAuth';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { TextField } from '@andritzot/metris-web-ui/inputs/textfield';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Stack } from '@andritzot/metris-web-ui/layout/stack';
import { Popover } from '@andritzot/metris-web-ui/utils/popover';
import SendOutlined from '@mui/icons-material/SendOutlined';
import { useCallback, useMemo, useState } from 'react';

export interface ItemCommentPopoverProps {
  itemId: number;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

function CommentList({ comments }: Readonly<{ comments: CommentDto[] }>) {
  if (comments.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No comments yet.
      </Typography>
    );
  }

  return (
    <>
      {comments.map((c) => (
        <Box key={c.idComment} sx={{ mb: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {c.user?.name ?? '—'}
          </Typography>
          <Typography variant="body2">{c.comment}</Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(c.timestamp).toLocaleString()}
          </Typography>
        </Box>
      ))}
    </>
  );
}

function useItemComments(itemId: number) {
  const editableRoadMap = useRoadmapEditStore((s) => s.editableRoadMap);

  return useMemo<CommentDto[]>(() => {
    for (const status of editableRoadMap.type?.roadmapStatus ?? []) {
      const found = status.roadmapStatusItems.find((i) => i.idRoadmapStatusItem === itemId);
      if (found) return found.comments ?? [];
    }
    return [];
  }, [editableRoadMap, itemId]);
}

function useAddComment(itemId: number) {
  const { user } = useAuth();
  const editableRoadMap = useRoadmapEditStore((s) => s.editableRoadMap);

  return useCallback(
    (comment: string) => {
      if (!comment.trim() || !user) return;

      const updatedStatuses = editableRoadMap.type?.roadmapStatus?.map((s) => ({
        ...s,
        roadmapStatusItems: s.roadmapStatusItems.map((item) => {
          if (item.idRoadmapStatusItem !== itemId) return item;
          return {
            ...item,
            comments: [
              ...(item.comments ?? []),
              {
                comment: comment.trim(),
                idComment: ((item.comments?.length ?? 0) + 1) * -1,
                idUser: user.idUser,
                timestamp: new Date().toISOString(),
                user,
              },
            ],
            edited: true,
          };
        }),
      }));

      if (updatedStatuses && editableRoadMap.type) {
        useRoadmapEditStore.setState({
          editableRoadMap: { ...editableRoadMap, type: { ...editableRoadMap.type, roadmapStatus: updatedStatuses } },
        });
      }
    },
    [user, editableRoadMap, itemId],
  );
}

export function ItemCommentPopover({ itemId, anchorEl, onClose }: Readonly<ItemCommentPopoverProps>) {
  const [newComment, setNewComment] = useState('');
  const { isLoggedIn } = useAuth();
  const comments = useItemComments(itemId);
  const addComment = useAddComment(itemId);

  const handleSubmit = () => {
    addComment(newComment);
    setNewComment('');
  };

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Box sx={{ p: 1.5, width: 320 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Comments
        </Typography>
        <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 1 }}>
          <CommentList comments={comments} />
        </Box>
        {isLoggedIn && (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'flex-end' }}>
            <TextField
              size="small"
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              multiline
              maxRows={3}
              fullWidth
              slotProps={{ htmlInput: { maxLength: 300 } }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
            <IconButton size="small" onClick={handleSubmit} disabled={!newComment.trim()}>
              <SendOutlined fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Box>
    </Popover>
  );
}
